import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon, MinusIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { controlDisabledClass, controlFocusClass, controlInvalidClass, type ControlSize } from './control-styles';
import { Label } from './label';

const checkboxSizeClass: Record<ControlSize, string> = {
  sm: 'size-3.5 [&_svg]:size-3',
  default: 'size-4 [&_svg]:size-3.5',
  lg: 'size-5 [&_svg]:size-4'
};

type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  controlSize?: ControlSize;
};

/**
 * Accessible checkbox (role="checkbox", Space toggles) with checked,
 * indeterminate, disabled, error and focus states driven by skin tokens.
 */
function Checkbox({ className, controlSize = 'default', ...props }: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      data-size={controlSize}
      className={cn(
        'peer group/checkbox border-input dark:bg-input/30 shrink-0 rounded-[4px] border bg-transparent shadow-xs transition-shadow outline-none',
        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary',
        'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary',
        controlFocusClass,
        controlInvalidClass,
        controlDisabledClass,
        checkboxSizeClass[controlSize],
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        <CheckIcon aria-hidden="true" className="group-data-[state=indeterminate]/checkbox:hidden" />
        <MinusIcon aria-hidden="true" className="hidden group-data-[state=indeterminate]/checkbox:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

type CheckboxFieldProps = Omit<CheckboxProps, 'className'> & {
  label: React.ReactNode;
  /** Styles the wrapping row. */
  className?: string;
  checkboxClassName?: string;
  labelClassName?: string;
};

/** Checkbox with a paired `Label htmlFor`; the label text is the accessible name. */
function CheckboxField({ label, id, className, checkboxClassName, labelClassName, ...props }: CheckboxFieldProps) {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  return <div data-slot="checkbox-field" className={cn('flex items-center gap-2', className)}>
    <Checkbox id={fieldId} className={checkboxClassName} {...props} />
    <Label htmlFor={fieldId} className={cn('font-normal', labelClassName)}>{label}</Label>
  </div>;
}

export { Checkbox, CheckboxField };
export type { CheckboxFieldProps };
export type { CheckboxProps };
